import React, { useRef } from 'react';
import {
    Button, Dialog, DialogActions, DialogTitle, Box, CircularProgress
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';

/**
 * PrintPreviewDialog — shows a PDF blob URL in a full-screen dialog with a Print button.
 *
 * Props:
 *   url      - blob URL string (truthy = open)
 *   onClose  - called when user dismisses the dialog (should revoke URL + clear state)
 *   title    - optional dialog title
 */
export default function PrintPreviewDialog({ url, onClose, title = 'Print Preview' }) {
    const iframeRef = useRef(null);
    const [iframeLoaded, setIframeLoaded] = React.useState(false);

    const handlePrint = () => {
        if (iframeRef.current?.contentWindow) {
            iframeRef.current.contentWindow.focus();
            iframeRef.current.contentWindow.print();
        }
    };

    const handleClose = () => {
        setIframeLoaded(false);
        onClose();
    };

    // Reset loaded state each time a new URL arrives
    React.useEffect(() => {
        setIframeLoaded(false);
    }, [url]);

    return (
        <Dialog
            open={Boolean(url)}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { height: '90vh' } }}
        >
            <DialogTitle sx={{ py: 1.5 }}>{title}</DialogTitle>

            <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative', mx: 2 }}>
                {!iframeLoaded && (
                    <Box
                        sx={{
                            position: 'absolute', inset: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            backgroundColor: 'background.paper',
                            zIndex: 1,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}
                {url && (
                    <iframe
                        ref={iframeRef}
                        src={url}
                        title={title}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        onLoad={() => setIframeLoaded(true)}
                    />
                )}
            </Box>

            <DialogActions sx={{ px: 2, py: 1.5 }}>
                <Button
                    variant="contained"
                    startIcon={<PrintIcon />}
                    onClick={handlePrint}
                    disabled={!iframeLoaded}
                >
                    Print
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<CloseIcon />}
                    onClick={handleClose}
                    color="inherit"
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
